import { api, type Schemas } from '@/api/client'

// Cancellation and plan change of the member's subscription. The components keep the camelCase shapes of the
// former edge functions; the API speaks snake_case.

export type ImpactBooking = { id: string; course_name: string; starts_at: string }
export type Replacement = { id: string; packageName: string; effectiveAt: string }

export type CancellationResult = {
  operationKey: string
  status: 'preparing' | 'uncertain' | 'applied' | 'failed'
  action: 'cancel' | 'resume'
  endsAt: string
  cancelledBookings: ImpactBooking[]
  replacementRemoved: boolean
  code: string | null
}

export type CancellationPreview = Partial<CancellationResult> & {
  previewId?: string
  previewedAt: string
  endsAt: string
  currentPriceId: string
  sourceConditionsVersionId: string | null
  affectedBookings: ImpactBooking[]
  replacement: Replacement | null
  alreadyCancelled?: boolean
  inProgress?: boolean
}

function toReplacement(row: Schemas['ReplacementResponse'] | null): Replacement | null {
  return row ? { id: row.id, packageName: row.package_name, effectiveAt: row.effective_at } : null
}

function toCancellationResult(row: Schemas['CancellationResultResponse']): CancellationResult {
  return {
    operationKey: row.operation_key,
    status: row.status as CancellationResult['status'],
    action: row.action as CancellationResult['action'],
    endsAt: row.ends_at,
    cancelledBookings: row.cancelled_bookings,
    replacementRemoved: row.replacement_removed,
    code: row.code ?? null,
  }
}

export async function previewCancellation(intent: 'cancel' | 'resume'): Promise<CancellationPreview> {
  const { data } = await api.POST('/api/v1/subscriptions/current/cancellation-previews', { body: { intent } })
  const row = data!
  return {
    previewId: row.preview_id ?? undefined,
    operationKey: row.operation_key ?? undefined,
    previewedAt: row.previewed_at ?? '',
    endsAt: row.ends_at ?? '',
    currentPriceId: row.current_price_id ?? '',
    sourceConditionsVersionId: row.source_conditions_version_id ?? null,
    affectedBookings: row.affected_bookings,
    replacement: toReplacement(row.replacement ?? null),
    alreadyCancelled: row.already_cancelled,
    inProgress: row.in_progress,
    status: (row.status ?? undefined) as CancellationResult['status'] | undefined,
    action: (row.action ?? undefined) as CancellationResult['action'] | undefined,
    cancelledBookings: row.cancelled_bookings ?? undefined,
    replacementRemoved: row.replacement_removed,
    code: row.code ?? null,
  }
}

export async function confirmCancellation(preview: CancellationPreview, action: 'cancel' | 'resume'): Promise<CancellationResult> {
  const { data } = await api.POST('/api/v1/subscriptions/current/cancellations', {
    body: {
      action,
      preview_id: preview.previewId ?? null,
      operation_key: preview.operationKey ?? null,
      previewed_at: preview.previewedAt || null,
      ends_at: preview.endsAt || null,
      current_price_id: preview.currentPriceId || null,
      source_conditions_version_id: preview.sourceConditionsVersionId,
      affected_bookings: preview.affectedBookings,
      replaces_change_id: preview.replacement?.id ?? null,
      confirm_consequences: true,
    },
  })
  return toCancellationResult(data!)
}

export async function checkCancellation(operationKey: string): Promise<CancellationResult> {
  const { data } = await api.POST('/api/v1/subscriptions/current/cancellation-status-checks', {
    body: { operation_key: operationKey },
  })
  return toCancellationResult(data!)
}

export type PlanChangePreview = {
  previewId: string
  currentPriceId: string
  sourceConditionsVersionId: string
  targetConditionsVersionId: string
  targetPriceId: string
  affectedBookings: ImpactBooking[]
  changeKind: 'upgrade' | 'same_price' | 'downgrade'
  replacement: Replacement | null
  amountDue: number
  currency: string
  effectiveAt: string
  prorationDate: number
}

export type PlanChangeResult = {
  status: string
  paymentUrl?: string | null
  cancelledBookings: ImpactBooking[]
  pendingBookingCancellations?: ImpactBooking[]
  replacementRemoved: boolean
}

export async function previewPlanChange(packageType: string): Promise<PlanChangePreview> {
  const { data } = await api.POST('/api/v1/subscriptions/current/plan-change-previews', { body: { package_type: packageType } })
  const row = data!
  return {
    previewId: row.preview_id,
    currentPriceId: row.current_price_id,
    sourceConditionsVersionId: row.source_conditions_version_id ?? '',
    targetConditionsVersionId: row.target_conditions_version_id ?? '',
    targetPriceId: row.target_price_id,
    affectedBookings: row.affected_bookings,
    changeKind: row.change_kind as PlanChangePreview['changeKind'],
    replacement: toReplacement(row.replacement ?? null),
    amountDue: row.amount_due,
    currency: row.currency,
    effectiveAt: row.effective_at,
    prorationDate: row.proration_date,
  }
}

export async function confirmPlanChange(
  packageType: string,
  preview: PlanChangePreview,
  confirmConsequences: boolean,
): Promise<PlanChangeResult> {
  const { data } = await api.POST('/api/v1/subscriptions/current/plan-changes', {
    body: {
      package_type: packageType,
      preview_id: preview.previewId,
      proration_date: preview.prorationDate,
      current_price_id: preview.currentPriceId,
      source_conditions_version_id: preview.sourceConditionsVersionId || null,
      target_price_id: preview.targetPriceId,
      target_conditions_version_id: preview.targetConditionsVersionId || null,
      affected_bookings: preview.affectedBookings,
      effective_at: preview.effectiveAt,
      amount_due: preview.amountDue,
      replaces_change_id: preview.replacement?.id ?? null,
      confirm_consequences: confirmConsequences,
    },
  })
  const row = data!
  return {
    status: row.status,
    paymentUrl: row.payment_url ?? null,
    cancelledBookings: row.cancelled_bookings,
    pendingBookingCancellations: row.pending_booking_cancellations,
    replacementRemoved: row.replacement_removed,
  }
}
